import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ActionsModal, type Action } from './ActionsModal.js';
import { Alert } from './Alert.js';
import { Button } from './Button.js';
import { cn } from './cn.js';
import { COLOR_CLASSES, ColorPicker } from './ColorPicker.js';
import { EmptyState } from './EmptyState.js';
import { Field } from './Field.js';
import { Icon } from './Icon.js';
import {
  DEFAULT_KANBAN_COLOR,
  collapsedCount,
  columnCount,
  describeMove,
  dropIndex,
  emptyColumn,
  kanbanReducer,
  shiftStage,
  undoMove,
  type KanbanColor,
  type KanbanColumn,
  type KanbanColumns,
  type KanbanMove,
  type KanbanPage,
  type KanbanStage,
  type KanbanStageInput,
} from './kanbanState.js';
import { Modal, ModalActions } from './Modal.js';
import { StageEventsEditor } from './StageEventsEditor.js';
import { useDragScroll } from './useDragScroll.js';
import {
  sameStageEvents,
  type StageEvent,
  type StageEventsConfig,
} from './stageEvents.js';

export {
  KANBAN_COLORS,
  collapsedForFocus,
  toggleCollapsed,
  type KanbanColor,
  type KanbanMove,
  type KanbanPage,
  type KanbanStage,
  type KanbanStageInput,
} from './kanbanState.js';

/**
 * O nome de uma fase com o ponto da cor dela — para quem mostra a fase FORA do
 * quadro: a coluna "Fase" da visão em tabela, um detalhe de cartão.
 *
 * Existe para a cor não ser reimplementada em cada tela. O ponto é mudo: quem
 * diz a fase é o nome.
 */
export function KanbanStageLabel({
  stage,
}: {
  stage: Pick<KanbanStage, 'name' | 'color'>;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className={cn(
          'size-2.5 shrink-0 rounded-pilula',
          COLOR_CLASSES[stage.color ?? DEFAULT_KANBAN_COLOR].dot,
        )}
      />
      <span className="truncate">{stage.name}</span>
    </span>
  );
}

/**
 * A casca do cartão: superfície, borda fina e sombra leve. A coluna é branca
 * como ele — só o cabeçalho leva a cor da fase —, então é a borda que o separa
 * do fundo; a sombra sozinha, branco sobre branco, some em tela de pouco
 * contraste.
 */
const CARD_SHELL =
  'rounded-borda border border-borda bg-superficie p-3.5 text-left shadow-cartao';

/** O botão só de ícone do cabeçalho da coluna — "+" e "…". */
const ICON_BUTTON = cn(
  'grid size-8 shrink-0 place-items-center rounded-borda text-texto-suave [--traco-icone:2.5]',
  'transition-colors hover:bg-superficie/70 hover:text-texto active:bg-superficie pointer-coarse:size-11',
);

/**
 * O que se faz com as FASES — cada função presente é uma ação oferecida.
 *
 * A permissão é do módulo, não do quadro: quem não pode criar fase recebe
 * `create` ausente, e o botão não existe. É o mesmo `can` de `@evogard/domain`
 * que o guard usa, decidido na tela que monta o quadro — o quadro não conhece
 * recurso nenhum. Esconder continua não sendo proteger: a rota do módulo
 * recusa de qualquer jeito.
 *
 * Toda função pode devolver promessa. O que ela rejeitar vira mensagem na
 * tela, com o `message` do erro — por isso a mensagem precisa ser frase para
 * quem usa, e não o texto técnico do servidor.
 */
export interface KanbanStageActions {
  create?: (input: KanbanStageInput) => Promise<void> | void;
  /** Nome e cor, juntos: é o mesmo formulário. */
  update?: (stageId: string, input: KanbanStageInput) => Promise<void> | void;
  /** Recebe a ordem nova, inteira. */
  reorder?: (stageIds: string[]) => Promise<void> | void;
  archive?: (stageId: string) => Promise<void> | void;
}

export interface KanbanProps<T> {
  /** O nome do quadro para quem usa leitor de tela — "Quadro de despesas". */
  label: string;
  /** Na ordem em que as colunas aparecem. Vazia é o estado de nascença. */
  stages: readonly KanbanStage[];
  cardId: (card: T) => string;
  /** Como o cartão é chamado em voz alta e nas mensagens — o protocolo, o título. */
  cardLabel: (card: T) => string;
  /**
   * Uma página de UMA coluna. `cursor` é `null` na primeira e, depois, o `next`
   * que a página anterior devolveu. O quadro pede a primeira de cada coluna ao
   * abrir e a seguinte quando o fim da coluna chega perto da vista.
   */
  loadPage: (stageId: string, cursor: string | null) => Promise<KanbanPage<T>>;
  /** O miolo do cartão. A casca, o foco e o arrastar são do quadro. */
  renderCard: (card: T) => ReactNode;
  /**
   * Muda quando o que está fora do quadro mudou — um filtro, a base, um cartão
   * criado. Todas as colunas recomeçam da primeira página.
   */
  reloadKey?: unknown;
  /** Clique (ou Enter) no cartão. Ausente, o cartão não é botão. */
  onOpenCard?: (card: T) => void;
  /**
   * Ausente, nada se arrasta. O quadro move na hora e desfaz se a promessa
   * for rejeitada — ver `KanbanMove` para o que os vizinhos significam.
   */
  onMoveCard?: (move: KanbanMove) => Promise<void> | void;
  /** O "+" de cada coluna. Quem abre o formulário é o módulo. */
  onCreateCard?: (stageId: string) => void;
  /** O rótulo do "+" — "Nova despesa". */
  createCardLabel?: string;
  stageActions?: KanbanStageActions;
  /**
   * Os eventos das fases. Presente, o formulário de fase ganha o
   * `StageEventsEditor` e o cabeçalho da coluna diz quantos a fase tem. O
   * quadro só os GUARDA na fase: quem dispara é quem muda a fase — ver
   * `stageEvents.ts`.
   */
  stageEvents?: StageEventsConfig;
  /** Busca e filtros, acima das colunas. */
  toolbar?: ReactNode;
  /** A frase da coluna vazia. */
  emptyColumnText?: string;
  /** A altura típica de um cartão, em px — só a primeira estimativa; depois mede. */
  estimatedCardHeight?: number;
  /**
   * As colunas que desenham RECOLHIDAS, numa faixa estreita com o nome, o
   * número e o alerta (28/10/2026). Quem guarda a lista é o módulo — nas
   * preferências da pessoa, ou o filtro de fase —, e o quadro só desenha.
   */
  collapsed?: readonly string[];
  /** Presente, a coluna ganha o botão de recolher e a faixa abre no clique. */
  onCollapse?: (stageId: string, collapsed: boolean) => void;
  /**
   * Quantos cartões uma coluna tem, sem trazê-los — o número da faixa. Com
   * ele, a coluna recolhida não carrega cartão nenhum; sem ele, carrega a
   * primeira página como as outras, só para saber o total.
   */
  countStage?: (stageId: string) => Promise<number>;
  /** A coluna para onde o quadro rola quando este valor muda — a fase do filtro. */
  scrollToStage?: string;
}

/** Nenhuma coluna recolhida — a mesma lista a cada render. */
const NONE: readonly string[] = [];

/** Quantos cartões antes do fim a página seguinte começa a ser pedida. */
const LOAD_AHEAD = 8;
const COLUMN_PREFIX = 'kanban-coluna:';

interface CardData {
  type: 'card';
  stageId: string;
}
interface ColumnData {
  type: 'column';
  stageId: string;
}

/**
 * O quadro genérico: colunas por fase, cartões que andam entre elas.
 *
 * ## Nasce sem fase, e diz isso
 *
 * Quadro novo não tem coluna nenhuma — as fases são do módulo e de quem o
 * configura, nunca uma lista que o código semeia (regra 14). Sem fase, a tela
 * não desenha um quadro vazio fingindo que a fila está zerada: diz que não há
 * fase e, para quem pode, oferece criar a primeira.
 *
 * ## Carrega por coluna, e desenha só o que se vê
 *
 * Cada coluna pede a própria primeira página e a seguinte quando o fim chega
 * perto (`LOAD_AHEAD`). Dentro da coluna, só os cartões visíveis viram nó no
 * DOM (`@tanstack/react-virtual`): mil cartões carregados são uns vinte
 * desenhados. A altura de cada um é medida depois de desenhado — cartão não
 * tem altura fixa.
 *
 * ## Arrasta com mouse, dedo e teclado
 *
 * `@dnd-kit`. No mouse, o arrasto começa depois de 6px, para o clique continuar
 * abrindo o cartão; no toque, depois de segurar 200ms, para a rolagem
 * continuar rolando. No teclado, **espaço** pega, as setas escolhem o lugar,
 * espaço solta e Esc cancela; Enter segue abrindo o cartão. O que acontece é
 * anunciado em português para quem usa leitor de tela.
 *
 * O cartão não desliza enquanto se arrasta: uma cópia segue o ponteiro
 * (`DragOverlay`) e uma linha marca onde ele vai cair. Empurrar os vizinhos
 * seria animar layout, e o `DESIGN.md` não anima layout.
 *
 * ## O movimento é otimista, e o desfazer é o inverso
 *
 * Soltou, o cartão já está no destino; `onMoveCard` roda depois. Se ele
 * rejeitar, o cartão volta para a posição de onde saiu e a recusa aparece no
 * alto do quadro. Ver `undoMove`.
 *
 * O miolo do cartão (`renderCard`) não deve ter botão nem link: a casca inteira
 * já é o alvo do clique e do arrasto, e controle dentro de controle é um nó que
 * o teclado não sabe resolver.
 */
export function Kanban<T>({
  label,
  stages,
  cardId,
  cardLabel,
  loadPage,
  renderCard,
  reloadKey,
  onOpenCard,
  onMoveCard,
  onCreateCard,
  createCardLabel = 'Novo cartão',
  stageActions = {},
  stageEvents,
  toolbar,
  emptyColumnText = 'Nada nesta fase agora.',
  estimatedCardHeight = 96,
  collapsed = NONE,
  onCollapse,
  countStage,
  scrollToStage,
}: KanbanProps<T>) {
  /* As funções do módulo costumam nascer a cada render; os efeitos leem a
     versão atual por referência para não recarregar o quadro por isso. */
  const cardIdRef = useRef(cardId);
  cardIdRef.current = cardId;
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;
  const stableCardId = useCallback((card: T) => cardIdRef.current(card), []);

  const reducer = useMemo(() => kanbanReducer<T>(stableCardId), [stableCardId]);
  const [columns, dispatch] = useReducer(reducer, stages, (initial) => {
    const state: KanbanColumns<T> = {};
    for (const s of initial) state[s.id] = emptyColumn<T>();
    return state;
  });
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const stageKey = stages.map((s) => s.id).join('\u0000');
  useEffect(() => {
    dispatch({ type: 'sync', stageIds: stageKey ? stageKey.split('\u0000') : [] });
  }, [stageKey]);

  /* Recomeça só quando a chave MUDOU. Um "pula a primeira vez" erra no modo
     estrito do React, que roda o efeito duas vezes ao montar: a segunda contava
     como mudança e recomeçava as colunas no meio da primeira busca. */
  const lastReloadKey = useRef(reloadKey);
  useEffect(() => {
    if (Object.is(lastReloadKey.current, reloadKey)) return;
    lastReloadKey.current = reloadKey;
    dispatch({ type: 'reset' });
  }, [reloadKey]);

  const load = useCallback(
    async (stageId: string, column: KanbanColumn<T>, cursor: string | null) => {
      const generation = column.generation;
      dispatch({ type: 'loading', stageId, generation });
      try {
        const page = await loadPageRef.current(stageId, cursor);
        dispatch({ type: 'loaded', stageId, generation, page, replace: cursor === null });
      } catch (e) {
        dispatch({ type: 'failed', stageId, generation, error: messageOf(e) });
      }
    },
    [],
  );

  /* Toda coluna que ainda não tem a primeira página, e não está buscando nem
     parada num erro, pede a sua — menos a recolhida, quando o módulo sabe
     contar sem trazer os cartões: ela pede quando abrir. */
  const skipCollapsed = countStage !== undefined;
  const collapsedKey = collapsed.join('\u0000');
  useEffect(() => {
    const folded = new Set(collapsedKey ? collapsedKey.split('\u0000') : []);
    for (const [stageId, column] of Object.entries(columns)) {
      if (skipCollapsed && folded.has(stageId)) continue;
      if (!column.loaded && column.status === 'idle') void load(stageId, column, null);
    }
  }, [columns, load, skipCollapsed, collapsedKey]);

  /*
   * O número das faixas: uma contagem por coluna recolhida que ainda não
   * carregou, refeita quando o recorte muda (`reloadKey`). A resposta de um
   * recorte velho é descartada pela geração.
   */
  const countStageRef = useRef(countStage);
  countStageRef.current = countStage;
  const [counts, setCounts] = useState<Record<string, number>>({});
  const asked = useRef(new Set<string>());
  const countGeneration = useRef(0);
  const lastCountKey = useRef(reloadKey);
  useEffect(() => {
    if (Object.is(lastCountKey.current, reloadKey)) return;
    lastCountKey.current = reloadKey;
    countGeneration.current += 1;
    asked.current = new Set();
    setCounts({});
  }, [reloadKey]);
  useEffect(() => {
    const count = countStageRef.current;
    if (!count) return;
    for (const stageId of collapsedKey ? collapsedKey.split('\u0000') : []) {
      if (asked.current.has(stageId) || columns[stageId]?.loaded) continue;
      asked.current.add(stageId);
      const generation = countGeneration.current;
      count(stageId).then(
        (n) => {
          if (generation === countGeneration.current)
            setCounts((c) => ({ ...c, [stageId]: n }));
        },
        /* Sem número, a faixa desenha sem ele — abrir a coluna mostra o erro. */
        () => undefined,
      );
    }
  }, [collapsedKey, columns, counts]);

  /* A fase do filtro: o quadro rola até a coluna dela, já aberta. */
  const boardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollToStage) return;
    const el = boardRef.current?.querySelector(
      `[data-kanban-stage="${CSS.escape(scrollToStage)}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [scrollToStage]);
  useDragScroll(boardRef);

  /** A recusa mais recente — de mover, de reordenar. Fica até alguém dispensar. */
  const [failure, setFailure] = useState<string | null>(null);
  const [creatingStage, setCreatingStage] = useState(false);
  const [actionsFor, setActionsFor] = useState<KanbanStage | null>(null);

  /* --------------------------------------------------------------------- */
  /* arrastar                                                              */

  const canMove = Boolean(onMoveCard);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [target, setTarget] = useState<{ stageId: string; index: number } | null>(null);

  const findCard = useCallback((id: string): { card: T; stageId: string } | null => {
    for (const [stageId, column] of Object.entries(columnsRef.current)) {
      const card = column.items.find((c) => cardIdRef.current(c) === id);
      if (card !== undefined) return { card, stageId };
    }
    return null;
  }, []);

  const stageName = useCallback(
    (stageId: string) => stages.find((s) => s.id === stageId)?.name ?? 'fase',
    [stages],
  );

  const targetOf = useCallback((event: DragOverEvent | DragEndEvent) => {
    const { active, over } = event;
    if (!over) return null;
    const data = over.data.current as CardData | ColumnData | undefined;
    if (!data) return null;
    const items = columnsRef.current[data.stageId]?.items ?? [];
    const id = String(active.id);
    if (data.type === 'column') {
      return {
        stageId: data.stageId,
        index: items.filter((c) => cardIdRef.current(c) !== id).length,
      };
    }
    const dragged = active.rect.current.translated;
    const below = dragged
      ? dragged.top + dragged.height / 2 > over.rect.top + over.rect.height / 2
      : false;
    return {
      stageId: data.stageId,
      index: dropIndex(items, String(over.id), id, below, cardIdRef.current),
    };
  }, []);

  const onDragStart = (event: DragStartEvent) => {
    setFailure(null);
    setActiveId(String(event.active.id));
  };

  const onDragOver = (event: DragOverEvent) => setTarget(targetOf(event));

  const finish = () => {
    setActiveId(null);
    setTarget(null);
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const drop = targetOf(event);
    finish();
    const found = findCard(String(event.active.id));
    if (!drop || !found || !onMoveCard) return;

    const request = {
      cardId: String(event.active.id),
      fromStageId: found.stageId,
      toStageId: drop.stageId,
      index: drop.index,
    };
    const move = describeMove(columnsRef.current, request, cardIdRef.current);
    if (!move) return;
    const originalIndex = columnsRef.current[found.stageId]!.items.findIndex(
      (c) => cardIdRef.current(c) === move.cardId,
    );

    dispatch({ type: 'move', ...move });
    try {
      await onMoveCard(move);
    } catch (e) {
      dispatch({ type: 'move', ...undoMove(move, originalIndex) });
      setFailure(
        `“${cardLabel(found.card)}” voltou para ${stageName(move.fromStageId)}. ${messageOf(e)}`,
      );
    }
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      const found = findCard(String(active.id));
      return found
        ? `Pegou “${cardLabel(found.card)}”, em ${stageName(found.stageId)}.`
        : 'Pegou o cartão.';
    },
    onDragOver: ({ active, over }) => {
      const found = findCard(String(active.id));
      const name = found ? `“${cardLabel(found.card)}”` : 'O cartão';
      if (!over) return `${name} está fora do quadro.`;
      const data = over.data.current as CardData | ColumnData | undefined;
      return data ? `${name} sobre ${stageName(data.stageId)}.` : undefined;
    },
    onDragEnd: ({ active, over }) => {
      const found = findCard(String(active.id));
      const name = found ? `“${cardLabel(found.card)}”` : 'O cartão';
      const data = over?.data.current as CardData | ColumnData | undefined;
      return data
        ? `${name} solto em ${stageName(data.stageId)}.`
        : `${name} solto fora do quadro. Nada mudou.`;
    },
    onDragCancel: ({ active }) => {
      const found = findCard(String(active.id));
      return `Movimento cancelado. ${found ? `“${cardLabel(found.card)}”` : 'O cartão'} ficou onde estava.`;
    },
  };

  const active = activeId ? findCard(activeId) : null;

  /* --------------------------------------------------------------------- */
  /* fases                                                                 */

  const run = async (work: () => Promise<void> | void) => {
    setFailure(null);
    try {
      await work();
    } catch (e) {
      setFailure(messageOf(e));
    }
  };

  const actionsOf = (stage: KanbanStage): Action[] => {
    const list: Action[] = [];
    const { update, reorder, archive } = stageActions;
    if (update) {
      list.push({
        label: stageEvents ? 'Editar nome, cor e eventos' : 'Editar nome e cor',
        icon: 'etiqueta',
        form: (close) => (
          <StageForm
            initial={{
              name: stage.name,
              color: stage.color ?? DEFAULT_KANBAN_COLOR,
              events: [...(stage.events ?? [])],
            }}
            stages={stages}
            stageEvents={stageEvents}
            ignoreId={stage.id}
            submitLabel="Salvar"
            createCardLabel={createCardLabel}
            emptyText={emptyColumnText}
            onSubmit={(input) => update(stage.id, input)}
            onDone={close}
          />
        ),
      });
    }
    if (reorder) {
      const left = shiftStage(stages, stage.id, -1);
      const right = shiftStage(stages, stage.id, 1);
      list.push(
        {
          label: 'Mover para a esquerda',
          icon: 'mover',
          disabled: left ? false : 'Já é a primeira fase.',
          run: () => left && void run(() => reorder(left)),
        },
        {
          label: 'Mover para a direita',
          icon: 'mover',
          disabled: right ? false : 'Já é a última fase.',
          run: () => right && void run(() => reorder(right)),
        },
      );
    }
    if (archive) {
      list.push({
        label: 'Arquivar fase',
        icon: 'lixeira',
        tone: 'perigo',
        form: (close) => (
          <ArchiveStageForm
            stage={stage}
            onSubmit={() => archive(stage.id)}
            onDone={close}
          />
        ),
      });
    }
    return list;
  };

  const hasStageActions = Boolean(
    stageActions.update || stageActions.reorder || stageActions.archive,
  );

  return (
    <div className="flex flex-col gap-3">
      {toolbar}

      {failure && (
        <Alert tone="erro" title="Não foi possível concluir.">
          <p>{failure}</p>
          <Button
            variant="discreto"
            size="sm"
            className="mt-1.5 -ml-2.75"
            onClick={() => setFailure(null)}
          >
            Entendi
          </Button>
        </Alert>
      )}

      {stages.length === 0 ? (
        <EmptyState
          icon="quadro"
          title="Este quadro ainda não tem fases"
          actions={
            stageActions.create && (
              <Button icon="mais" onClick={() => setCreatingStage(true)}>
                Criar a primeira fase
              </Button>
            )
          }
        >
          {stageActions.create
            ? 'As fases são as colunas do quadro: cada cartão está em uma delas e anda para a próxima conforme o trabalho avança. Comece pela primeira.'
            : 'As fases são as colunas do quadro, e quem configura este quadro ainda não criou nenhuma. Até lá, não há onde um cartão entrar.'}
        </EmptyState>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={cardsFirst}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={(e) => void onDragEnd(e)}
          onDragCancel={finish}
          accessibility={{
            announcements,
            screenReaderInstructions: {
              draggable:
                'Para mover o cartão, aperte espaço. Use as setas para escolher o lugar e aperte espaço de novo para soltar, ou Esc para cancelar. Enter abre o cartão.',
            },
          }}
        >
          <div
            ref={boardRef}
            className="rolagem-chrome overflow-x-auto pb-2"
            role="region"
            aria-label={label}
          >
            {/* Largura FIXA por coluna, tenha o quadro uma fase ou vinte: uma
                fase sozinha esticada na tela inteira vira uma faixa de cartões
                largos demais para ler, e a coluna mudaria de tamanho a cada
                fase criada. Passou da tela, o quadro rola de lado. */}
            <div className="flex items-start gap-4.5">
              {stages.map((stage) =>
                collapsed.includes(stage.id) ? (
                  <CollapsedColumn
                    key={stage.id}
                    stage={stage}
                    count={collapsedCount(columns[stage.id], counts[stage.id])}
                    onOpen={onCollapse && (() => onCollapse(stage.id, false))}
                  />
                ) : (
                  <StageColumn
                    key={stage.id}
                    stage={stage}
                    column={columns[stage.id]}
                    cardId={stableCardId}
                    cardLabel={cardLabel}
                    renderCard={renderCard}
                    onOpenCard={onOpenCard}
                    canMove={canMove}
                    activeId={activeId}
                    dropAt={target?.stageId === stage.id ? target.index : null}
                    emptyText={emptyColumnText}
                    estimatedCardHeight={estimatedCardHeight}
                    onLoad={(column, cursor) => void load(stage.id, column, cursor)}
                    onCreateCard={onCreateCard && (() => onCreateCard(stage.id))}
                    createCardLabel={createCardLabel}
                    onActions={hasStageActions ? () => setActionsFor(stage) : undefined}
                    showEvents={Boolean(stageEvents)}
                    onCollapse={onCollapse && (() => onCollapse(stage.id, true))}
                  />
                ),
              )}

              {stageActions.create && (
                <button
                  type="button"
                  onClick={() => setCreatingStage(true)}
                  className={cn(
                    'flex h-11 w-44 shrink-0 items-center justify-center gap-2 rounded-cartao',
                    'border border-dashed border-borda-forte text-sm text-texto-suave',
                    'transition-colors hover:border-primaria-texto hover:text-texto active:bg-superficie-suave',
                  )}
                >
                  <Icon nome="mais" size={16} />
                  Nova fase
                </button>
              )}
            </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {active && (
              <div
                className={cn(
                  CARD_SHELL,
                  'w-72.5 cursor-grabbing ring-1 ring-borda-forte',
                )}
              >
                {renderCard(active.card)}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {stageActions.create && (
        <Modal
          open={creatingStage}
          onOpenChange={setCreatingStage}
          title="Nova fase"
          descricao="Ela entra como a última coluna do quadro. A ordem se muda depois, pelas ações da fase."
          size="lg"
          tone="destaque"
          icon="quadro"
        >
          {creatingStage && (
            <StageForm
              initial={{ name: '', color: DEFAULT_KANBAN_COLOR, events: [] }}
              stages={stages}
              stageEvents={stageEvents}
              submitLabel="Criar fase"
              createCardLabel={createCardLabel}
              emptyText={emptyColumnText}
              onSubmit={stageActions.create}
              onDone={() => setCreatingStage(false)}
            />
          )}
        </Modal>
      )}

      <ActionsModal
        open={actionsFor !== null}
        onOpenChange={(open) => !open && setActionsFor(null)}
        title={actionsFor ? `Fase “${actionsFor.name}”` : ''}
        icon="quadro"
        actions={actionsFor ? actionsOf(actionsFor) : []}
      />
    </div>
  );
}

/**
 * As fases de um quadro editadas FORA dele — para a visão em tabela da mesma
 * tela (08/10/2026).
 *
 * No quadro, o nome, a cor e os eventos de uma fase se editam pelo "…" do
 * cabeçalho da coluna. A tabela não tem coluna de fase, e as telas que
 * alternam entre as duas visões deixavam quem estava na tabela sem caminho
 * nenhum até o editor. Este diálogo é a mesma ação, com o MESMO `StageForm` —
 * a prévia, a recusa de nome repetido e o editor de eventos não têm segunda
 * cópia para divergir.
 *
 * Ele não vira ação da LINHA da tabela de propósito: a linha é um cartão, e
 * "editar a fase" a partir dele mudaria a fase de todos os cartões dela.
 *
 * Só edita. Criar, arquivar e reordenar continuam sendo do quadro, que é onde
 * a coluna existe e a ordem se vê.
 */
export function KanbanStagesModal({
  open,
  onOpenChange,
  stages,
  update,
  stageEvents,
  createCardLabel = 'Novo cartão',
  emptyColumnText = 'Nada nesta fase agora.',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: readonly KanbanStage[];
  update: NonNullable<KanbanStageActions['update']>;
  stageEvents?: StageEventsConfig;
  createCardLabel?: string;
  emptyColumnText?: string;
}) {
  const actions: Action[] = stages.map((stage) => {
    const events = stage.events?.length ?? 0;
    return {
      label:
        stageEvents && events > 0
          ? `${stage.name} · ${events === 1 ? '1 evento' : `${events} eventos`}`
          : stage.name,
      icon: 'etiqueta',
      form: (close) => (
        <StageForm
          initial={{
            name: stage.name,
            color: stage.color ?? DEFAULT_KANBAN_COLOR,
            events: [...(stage.events ?? [])],
          }}
          stages={stages}
          stageEvents={stageEvents}
          ignoreId={stage.id}
          submitLabel="Salvar"
          createCardLabel={createCardLabel}
          emptyText={emptyColumnText}
          onSubmit={(input) => update(stage.id, input)}
          onDone={close}
        />
      ),
    };
  });

  return (
    <ActionsModal
      open={open}
      onOpenChange={onOpenChange}
      title="Fases do quadro"
      descricao={
        stageEvents
          ? 'Escolha a fase para mudar nome, cor e eventos.'
          : 'Escolha a fase para mudar nome e cor.'
      }
      icon="quadro"
      actions={actions}
    />
  );
}

/**
 * Cartão antes de coluna.
 *
 * O ponteiro sobre um cartão está, ao mesmo tempo, sobre a coluna que o
 * contém; o cartão é a resposta mais precisa. A coluna só vence onde não há
 * cartão — o espaço vazio abaixo do último, ou a coluna sem nenhum. Sem
 * ponteiro (teclado), vale a interseção do retângulo arrastado.
 */
const cardsFirst: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  const hits = pointer.length > 0 ? pointer : rectIntersection(args);
  const cards = hits.filter(
    (h) =>
      (h.data?.droppableContainer?.data.current as CardData | undefined)?.type === 'card',
  );
  return cards.length > 0 ? cards : hits;
};

/** Sem deslizar os vizinhos — a linha de destino faz esse papel. */
const noShift = () => null;

function StageColumn<T>({
  stage,
  column,
  cardId,
  cardLabel,
  renderCard,
  onOpenCard,
  canMove,
  activeId,
  dropAt,
  emptyText,
  estimatedCardHeight,
  onLoad,
  onCreateCard,
  createCardLabel,
  onActions,
  showEvents,
  onCollapse,
}: {
  stage: KanbanStage;
  column: KanbanColumn<T> | undefined;
  cardId: (card: T) => string;
  cardLabel: (card: T) => string;
  renderCard: (card: T) => ReactNode;
  onOpenCard?: (card: T) => void;
  canMove: boolean;
  activeId: string | null;
  /** A posição onde o cartão arrastado cairia nesta coluna, ou `null`. */
  dropAt: number | null;
  emptyText: string;
  estimatedCardHeight: number;
  onLoad: (column: KanbanColumn<T>, cursor: string | null) => void;
  onCreateCard?: () => void;
  createCardLabel: string;
  onActions?: () => void;
  showEvents: boolean;
  onCollapse?: () => void;
}) {
  const items = column?.items ?? [];
  const count = columnCount(column);
  const { setNodeRef, isOver } = useDroppable({
    id: `${COLUMN_PREFIX}${stage.id}`,
    data: { type: 'column', stageId: stage.id } satisfies ColumnData,
  });

  /*
   * O destino como o cartão que vai ficar DEPOIS dele — a linha é desenhada na
   * borda de cima desse cartão, ou embaixo do último quando ele cai no fim.
   * `dropAt` é contado sem o cartão arrastado, e a lista desenhada ainda o tem.
   */
  const rest = activeId ? items.filter((c) => cardId(c) !== activeId) : items;
  const nextCard = dropAt === null ? undefined : rest[dropAt];
  const lineBefore = nextCard === undefined ? null : cardId(nextCard);
  const lineAtEnd = dropAt !== null && nextCard === undefined;

  return (
    <section
      data-kanban-stage={stage.id}
      className="flex w-80 shrink-0 scroll-m-6 flex-col rounded-cartao border border-borda bg-superficie"
      aria-label={stage.name}
    >
      <ColumnHeader
        name={stage.name}
        color={stage.color}
        count={count}
        alert={stage.alert}
        events={showEvents ? (stage.events?.length ?? 0) : undefined}
        createLabel={onCreateCard && `${createCardLabel} em ${stage.name}`}
        onCreate={onCreateCard}
        onActions={onActions}
        actionsLabel={`Ações da fase ${stage.name}`}
        onCollapse={onCollapse}
        collapseLabel={`Recolher a coluna ${stage.name}`}
      />

      {/*
       * O mesmo respiro acima e abaixo dos cartões (28/09/2026). Só havia o de
       * cima, e a coluna sem o "+" (o quadro de Pagamentos) encostava o último
       * cartão na borda de baixo. A coluna abraça o conteúdo (`items-start` no
       * quadro), então espaço igual dos dois lados É o centro da área abaixo do
       * cabeçalho — sem altura fixa, e cada coluna na própria altura.
       */}
      <div
        ref={setNodeRef}
        className={cn(
          'mx-3.5 my-3 rounded-borda transition-colors',
          isOver && items.length === 0 && 'bg-superficie-suave',
        )}
      >
        {!column || (!column.loaded && column.status !== 'error') ? (
          <ColumnSkeleton />
        ) : !column.loaded && column.status === 'error' ? (
          <ColumnError message={column.error} onRetry={() => onLoad(column, null)} />
        ) : items.length === 0 ? (
          <p
            className={cn(
              'rounded-borda px-1 py-6 text-xs text-texto-suave',
              lineAtEnd && 'outline-2 outline-dashed outline-primaria-texto',
            )}
          >
            {emptyText}
            {canMove && ' Arraste um cartão para cá.'}
          </p>
        ) : (
          <SortableContext
            id={stage.id}
            items={items.map(cardId)}
            strategy={noShift}
            disabled={!canMove}
          >
            <VirtualCards
              column={column}
              stageId={stage.id}
              cardId={cardId}
              cardLabel={cardLabel}
              renderCard={renderCard}
              onOpenCard={onOpenCard}
              canMove={canMove}
              activeId={activeId}
              lineBefore={lineBefore}
              lineAtEnd={lineAtEnd}
              estimatedCardHeight={estimatedCardHeight}
              onLoadMore={() => onLoad(column, column.next)}
            />
          </SortableContext>
        )}
      </div>

      {onCreateCard && (
        <Button
          variant="discreto"
          size="sm"
          icon="mais"
          className="mx-3.5 mb-3 w-auto"
          onClick={onCreateCard}
        >
          {createCardLabel}
        </Button>
      )}
    </section>
  );
}

/**
 * A coluna recolhida (28/10/2026): uma faixa estreita, na altura de uma coluna
 * curta, com o ponto de cor, o número, o alerta do SLA e o nome com as
 * letras em pé, uma embaixo da outra (pedido do dono, 28/10/2026).
 * Existe para o quadro de onze fases caber na tela — quem trabalha nas últimas
 * não rola até o fim para chegar à dele.
 *
 * O alerta fica à mostra de propósito: recolher esconde os cartões, não o
 * prazo estourado. A faixa inteira é o botão de abrir.
 */
function CollapsedColumn({
  stage,
  count,
  onOpen,
}: {
  stage: KanbanStage;
  count: string | null;
  onOpen?: () => void;
}) {
  const colors = COLOR_CLASSES[stage.color ?? DEFAULT_KANBAN_COLOR];
  const label = [
    `Abrir a coluna ${stage.name}`,
    count !== null && `${count} ${count === '1' ? 'cartão' : 'cartões'}`,
    stage.alert,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <section
      data-kanban-stage={stage.id}
      aria-label={stage.name}
      className="shrink-0 scroll-m-6"
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        aria-label={label}
        title={stage.name}
        className={cn(
          'flex min-h-72 w-12 flex-col items-center gap-2 rounded-cartao border border-borda py-3',
          'transition-colors hover:border-borda-forte disabled:cursor-default',
          colors.column,
        )}
      >
        <span
          aria-hidden
          className={cn('size-2.5 shrink-0 rounded-pilula', colors.dot)}
        />
        {count !== null && (
          <span className="shrink-0 rounded-pilula bg-superficie px-1.5 py-0.5 text-xs font-medium tabular-nums text-texto-suave">
            {count}
          </span>
        )}
        {stage.alert && (
          <span
            title={stage.alert}
            className="grid size-6 shrink-0 place-items-center rounded-pilula bg-erro-suave"
          >
            <Icon nome="relogio" size={16} className="size-3.5 text-erro" />
          </span>
        )}
        <span className="text-xs leading-none font-semibold whitespace-nowrap [text-orientation:upright] [writing-mode:vertical-rl]">
          {stage.name}
        </span>
      </button>
    </section>
  );
}

/**
 * O cabeçalho de uma coluna: ponto de cor, nome, contagem e as duas ações.
 *
 * É peça própria porque a prévia do formulário de fase desenha o MESMO
 * cabeçalho — duas cópias divergiriam na primeira mudança de uma delas, e a
 * prévia passaria a mostrar uma coluna que o quadro não desenha.
 *
 * Na prévia (`preview`) os botões são desenho e não controle: não fazem nada,
 * então não recebem foco nem nome acessível.
 */
function ColumnHeader({
  name,
  color,
  count,
  alert,
  createLabel,
  onCreate,
  onActions,
  actionsLabel,
  events,
  onCollapse,
  collapseLabel,
  preview = false,
}: {
  name: ReactNode;
  color: KanbanColor | undefined;
  count: string | null;
  alert?: string;
  /** Quantos eventos a fase tem — ausente quando o quadro não usa eventos. */
  events?: number;
  createLabel?: string;
  onCreate?: () => void;
  onActions?: () => void;
  actionsLabel?: string;
  /** O botão de recolher a coluna — o quadro que não recolhe não o recebe. */
  onCollapse?: () => void;
  collapseLabel?: string;
  preview?: boolean;
}) {
  const colors = COLOR_CLASSES[color ?? DEFAULT_KANBAN_COLOR];
  const icon = (
    nome: 'mais' | 'reticencias' | 'seta-esquerda',
    label: string | undefined,
    run?: () => void,
  ) =>
    preview ? (
      <span className={ICON_BUTTON}>
        <Icon nome={nome} size={20} />
      </span>
    ) : (
      run && (
        <button type="button" onClick={run} aria-label={label} className={ICON_BUTTON}>
          <Icon nome={nome} size={20} />
        </button>
      )
    );

  return (
    <header
      className={cn(
        'flex items-center gap-2 rounded-t-[calc(var(--radius-cartao)-1px)] py-2 pr-2 pl-3.5',
        colors.column,
      )}
    >
      <span aria-hidden className={cn('size-2.5 shrink-0 rounded-pilula', colors.dot)} />
      <h2 className="min-w-0 truncate text-sm font-semibold">{name}</h2>
      {count !== null && (
        <span className="shrink-0 rounded-pilula bg-superficie px-2 py-0.5 text-xs font-medium tabular-nums text-texto-suave">
          {count}
        </span>
      )}
      {alert && (
        <span className="flex shrink-0 items-center gap-1 rounded-pilula bg-erro-suave px-2 py-0.5 text-xs font-medium text-texto">
          <Icon nome="relogio" size={16} className="size-3.5 text-erro" />
          {alert}
        </span>
      )}
      <span className="flex-1" />
      {events !== undefined && events > 0 && (
        <span
          title={events === 1 ? '1 evento ao entrar' : `${events} eventos ao entrar`}
          className="flex shrink-0 items-center gap-1 text-xs font-medium tabular-nums text-texto-suave"
        >
          <Icon nome="tomada" size={16} />
          {events}
          <span className="sr-only">{events === 1 ? 'evento' : 'eventos'} ao entrar</span>
        </span>
      )}
      {icon('mais', createLabel, onCreate)}
      {icon('reticencias', actionsLabel, onActions)}
      {!preview && icon('seta-esquerda', collapseLabel, onCollapse)}
    </header>
  );
}

function VirtualCards<T>({
  column,
  stageId,
  cardId,
  cardLabel,
  renderCard,
  onOpenCard,
  canMove,
  activeId,
  lineBefore,
  lineAtEnd,
  estimatedCardHeight,
  onLoadMore,
}: {
  column: KanbanColumn<T>;
  stageId: string;
  cardId: (card: T) => string;
  cardLabel: (card: T) => string;
  renderCard: (card: T) => ReactNode;
  onOpenCard?: (card: T) => void;
  canMove: boolean;
  activeId: string | null;
  lineBefore: string | null;
  lineAtEnd: boolean;
  estimatedCardHeight: number;
  onLoadMore: () => void;
}) {
  const items = column.items;
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedCardHeight,
    getItemKey: (index) => cardId(items[index]!),
    overscan: 6,
    gap: 10,
  });
  const visible = virtualizer.getVirtualItems();
  const lastVisible = visible.at(-1)?.index ?? -1;

  const hasMore = column.next !== null;
  const idle = column.status === 'idle';
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  useEffect(() => {
    if (hasMore && idle && lastVisible >= items.length - LOAD_AHEAD)
      onLoadMoreRef.current();
  }, [hasMore, idle, lastVisible, items.length]);

  return (
    <div
      ref={scrollRef}
      className="rolagem-chrome -m-1 max-h-[calc(100dvh-18rem)] min-h-24 overflow-y-auto overscroll-contain p-1"
    >
      <ol className="relative" style={{ height: virtualizer.getTotalSize() }}>
        {visible.map((row) => {
          const card = items[row.index]!;
          const id = cardId(card);
          return (
            <li
              key={row.key}
              data-index={row.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full"
              style={{ transform: `translateY(${row.start}px)` }}
            >
              <CardShell
                id={id}
                stageId={stageId}
                label={cardLabel(card)}
                canMove={canMove}
                dragging={activeId === id}
                lineAbove={lineBefore === id}
                lineBelow={lineAtEnd && row.index === items.length - 1}
                onOpen={onOpenCard && (() => onOpenCard(card))}
              >
                {renderCard(card)}
              </CardShell>
            </li>
          );
        })}
      </ol>

      {column.status === 'loading' && (
        <p className="py-3 text-center text-xs text-texto-suave" aria-live="polite">
          Carregando mais…
        </p>
      )}
      {column.status === 'error' && (
        <ColumnError message={column.error} onRetry={onLoadMore} compact />
      )}
    </div>
  );
}

function CardShell({
  id,
  stageId,
  label,
  canMove,
  dragging,
  lineAbove,
  lineBelow,
  onOpen,
  children,
}: {
  id: string;
  stageId: string;
  label: string;
  canMove: boolean;
  dragging: boolean;
  lineAbove: boolean;
  lineBelow: boolean;
  onOpen?: () => void;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef } = useSortable({
    id,
    data: { type: 'card', stageId } satisfies CardData,
    disabled: !canMove,
    attributes: { roleDescription: 'cartão arrastável' },
  });
  const interactive = canMove || Boolean(onOpen);

  return (
    <div
      ref={setNodeRef}
      /* Os atributos do dnd-kit só quando arrasta: com `disabled` eles trazem
         `aria-disabled="true"` e "cartão arrastável", e o quadro só de leitura
         anunciava como desabilitado um cartão que abre no clique. */
      {...(canMove ? attributes : {})}
      {...(canMove ? listeners : {})}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={label}
      onClick={onOpen}
      onKeyDown={(e) => {
        listeners?.onKeyDown?.(e);
        if (e.key === 'Enter' && onOpen && !e.defaultPrevented) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        CARD_SHELL,
        'relative w-full',
        interactive && 'transition-shadow hover:ring-1 hover:ring-borda-forte',
        canMove && 'cursor-grab touch-manipulation',
        !canMove && onOpen && 'cursor-pointer',
        dragging && 'opacity-40',
        lineAbove &&
          'before:absolute before:inset-x-0 before:-top-[5px] before:h-0.5 before:rounded-full before:bg-primaria-texto',
        lineBelow &&
          'after:absolute after:inset-x-0 after:-bottom-[5px] after:h-0.5 after:rounded-full after:bg-primaria-texto',
      )}
    >
      {children}
    </div>
  );
}

function ColumnSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy>
      <span className="esqueleto block h-24 rounded-borda" />
      <span className="esqueleto block h-20 rounded-borda" />
      <span className="sr-only">Carregando a fase…</span>
    </div>
  );
}

function ColumnError({
  message,
  onRetry,
  compact = false,
}: {
  message: string | null;
  onRetry: () => void;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-1.5 text-xs',
        compact ? 'py-3' : 'py-6',
      )}
    >
      <p className="text-erro">
        {compact ? 'A página seguinte não carregou.' : 'Esta fase não carregou.'}{' '}
        {message}
      </p>
      <Button variant="contorno" size="sm" icon="atualizar" onClick={onRetry}>
        Tentar de novo
      </Button>
    </div>
  );
}

/**
 * O nome e a cor de uma fase, para criar ou editar — com a prévia da coluna.
 *
 * Nome repetido é recusado aqui, sem ida ao servidor: duas colunas chamadas
 * "Aprovação" fazem o cartão "estar em Aprovação" deixar de dizer onde ele
 * está. A comparação ignora caixa e espaço nas pontas.
 *
 * A cor é um grupo de rádios nativo desenhado como amostras: as setas trocam
 * de cor, o leitor de tela diz "Azul, 2 de 10", e nenhum código de teclado
 * precisou ser escrito para isso.
 *
 * ## A prévia é a coluna de verdade, em miniatura
 *
 * Mesmo `ColumnHeader`, mesmas classes de cor, mesma frase de coluna vazia e o
 * mesmo rótulo do botão de novo cartão que o quadro vai desenhar. Ela fica ao
 * lado do formulário quando há espaço (o diálogo de criar) e embaixo quando
 * não há (o painel de editar, mais estreito) — a pergunta é o tamanho do
 * CONTÊINER, não o da tela, por isso `@container`.
 *
 * Ela é desenho: `aria-hidden`, porque quem usa leitor de tela já ouviu o nome
 * e a cor escolhidos nos próprios campos.
 */
function StageForm({
  initial,
  stages,
  stageEvents,
  ignoreId,
  submitLabel,
  createCardLabel,
  emptyText,
  onSubmit,
  onDone,
}: {
  initial: KanbanStageInput;
  stages: readonly KanbanStage[];
  stageEvents?: StageEventsConfig;
  ignoreId?: string;
  submitLabel: string;
  createCardLabel: string;
  emptyText: string;
  onSubmit: (input: KanbanStageInput) => Promise<void> | void;
  onDone: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [color, setColor] = useState<KanbanColor>(initial.color);
  const [events, setEvents] = useState<StageEvent[]>(initial.events ?? []);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async () => {
    const clean = name.trim();
    const sameEvents = !stageEvents || sameStageEvents(events, initial.events ?? []);
    if (clean === initial.name.trim() && color === initial.color && sameEvents)
      return onDone();
    const taken = stages.some(
      (s) =>
        s.id !== ignoreId &&
        s.name.trim().toLocaleLowerCase('pt-BR') === clean.toLocaleLowerCase('pt-BR'),
    );
    if (taken) return setError('Já existe uma fase com esse nome neste quadro.');
    setError(null);
    setPending(true);
    try {
      await onSubmit(
        stageEvents ? { name: clean, color, events } : { name: clean, color },
      );
      onDone();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      className="@container flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="grid gap-5 @xl:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <Field
            label="Nome da fase"
            required
            autoFocus
            maxLength={60}
            placeholder="Ex.: Em análise"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={error ?? undefined}
          />

          <ColorPicker value={color} onChange={setColor} />
        </div>

        <div aria-hidden className="flex flex-col gap-2">
          <p className="text-xs font-medium text-texto-suave">Prévia</p>
          <div className="rounded-cartao border border-borda bg-superficie">
            <ColumnHeader
              name={
                name.trim() || (
                  <span className="font-normal text-texto-suave">Nome da fase</span>
                )
              }
              color={color}
              count="0"
              preview
            />
            <p className="px-4.5 py-5 text-xs text-texto-suave">{emptyText}</p>
            <span className="mb-3.5 flex h-[1.875rem] items-center justify-center gap-1.5 text-xs font-medium text-texto-suave">
              <Icon nome="mais" size={16} />
              {createCardLabel}
            </span>
          </div>
        </div>
      </div>

      {stageEvents && (
        <StageEventsEditor config={stageEvents} events={events} onChange={setEvents} />
      )}

      <ModalActions className="border-t border-borda-suave pt-4">
        <Button type="button" variant="contorno" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" loading={pending} disabled={!name.trim()}>
          {submitLabel}
        </Button>
      </ModalActions>
    </form>
  );
}

function ArchiveStageForm({
  stage,
  onSubmit,
  onDone,
}: {
  stage: KanbanStage;
  onSubmit: () => Promise<void> | void;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">
        A fase <strong className="font-medium">{stage.name}</strong> sai do quadro.
      </p>
      {error && <Alert tone="erro">{error}</Alert>}
      <div className="flex gap-2">
        <Button
          variant="perigo"
          loading={pending}
          onClick={async () => {
            setError(null);
            setPending(true);
            try {
              await onSubmit();
              onDone();
            } catch (e) {
              setError(messageOf(e));
            } finally {
              setPending(false);
            }
          }}
        >
          Arquivar fase
        </Button>
        <Button variant="contorno" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Tente de novo em instantes.';
}
